import {
	AlertTriangle,
	ChevronRight,
	FolderTree,
	Hash,
	Link2,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { api } from "../api/client";
import { MappingsTab } from "../components/categories/MappingsTab";
import { MissingMappingsTab } from "../components/categories/MissingMappingsTab";
import { TaxonomyTree } from "../components/categories/TaxonomyTree";
import { PageHeader } from "../components/layout/PageHeader";
import type {
	CategoryDetail,
	MappingStats,
	TaxonomyCategory,
} from "../types/categories";

type Tab = "taxonomy" | "mappings" | "missing";

const tabs: {
	id: Tab;
	label: string;
	icon: typeof FolderTree;
}[] = [
	{ id: "taxonomy", label: "Categories", icon: FolderTree },
	{ id: "mappings", label: "Mappings", icon: Link2 },
	{ id: "missing", label: "Missing Mappings", icon: AlertTriangle },
];

const Categories = () => {
	const [activeTab, setActiveTab] = useState<Tab>("taxonomy");
	const [tree, setTree] = useState<TaxonomyCategory[]>([]);
	const [flatList, setFlatList] = useState<TaxonomyCategory[]>([]);
	const [selectedDetail, setSelectedDetail] = useState<CategoryDetail | null>(
		null,
	);
	const [selectedId, setSelectedId] = useState<number | null>(null);
	const [treeLoading, setTreeLoading] = useState(true);
	const [stats, setStats] = useState<MappingStats | null>(null);

	// Flatten tree for search
	const flatten = useCallback(
		(nodes: TaxonomyCategory[]): TaxonomyCategory[] => {
			const result: TaxonomyCategory[] = [];
			for (const node of nodes) {
				result.push(node);
				if (node.children.length > 0) result.push(...flatten(node.children));
			}
			return result;
		},
		[],
	);

	// Fetch category tree
	useEffect(() => {
		const fetchTree = async () => {
			setTreeLoading(true);
			try {
				const { data } = await api.categories.tree.get();
				const treeData = (data ?? []) as unknown as TaxonomyCategory[];
				setTree(treeData);
				setFlatList(flatten(treeData));
			} catch {
				setTree([]);
				setFlatList([]);
			} finally {
				setTreeLoading(false);
			}
		};
		fetchTree();
	}, [flatten]);

	// Fetch stats
	useEffect(() => {
		const fetchStats = async () => {
			try {
				const { data } = await api["category-mappings"].stats.get();
				if (data) setStats(data as unknown as MappingStats);
			} catch {
				/* ignore */
			}
		};
		fetchStats();
	}, []);

	// Fetch category detail when selection changes
	const handleSelect = async (cat: TaxonomyCategory) => {
		setSelectedId(cat.id);
		try {
			const { data } = await api
				.categories({ id: String(cat.id) })
				.detail.get();
			if (data) setSelectedDetail(data as unknown as CategoryDetail);
		} catch {
			setSelectedDetail(null);
		}
	};

	// Find children in tree for the detail panel
	const findChildren = (
		nodes: TaxonomyCategory[],
		parentId: number,
	): TaxonomyCategory[] => {
		for (const node of nodes) {
			if (node.id === parentId) return node.children;
			const found = findChildren(node.children, parentId);
			if (found.length > 0) return found;
		}
		return [];
	};

	return (
		<div className="space-y-6">
			<PageHeader
				kicker="Menu · Categories"
				title={`${stats?.totalCategories?.toLocaleString("en-US") ?? "…"} categories.`}
				qualifier="And the keywords that fill them."
				description="Manage categories and keyword mappings."
			/>

			{/* Tabs */}
			<div className="border-b border-slate-200">
				<nav className="flex gap-1" aria-label="Tabs">
					{tabs.map((tab) => (
						<button
							key={tab.id}
							type="button"
							onClick={() => setActiveTab(tab.id)}
							className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-all ${
								activeTab === tab.id
									? "border-primary text-primary"
									: "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
							}`}
						>
							<tab.icon className="w-4 h-4" />
							{tab.label}
							{tab.id === "missing" && stats && stats.missingMappings > 0 && (
								<span className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-danger/10 text-danger">
									{stats.missingMappings}
								</span>
							)}
							{tab.id === "mappings" && stats && (
								<span className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-primary/10 text-primary">
									{stats.totalMappings}
								</span>
							)}
						</button>
					))}
				</nav>
			</div>

			{/* Tab content */}
			{activeTab === "taxonomy" && (
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
					{/* Tree panel */}
					<div className="lg:col-span-1 bg-white rounded-xl border border-slate-200/60 overflow-hidden h-[calc(100vh-280px)]">
						{treeLoading ? (
							<div className="flex items-center justify-center h-full">
								<div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
							</div>
						) : (
							<TaxonomyTree
								tree={tree}
								flatList={flatList}
								selectedId={selectedId}
								onSelect={handleSelect}
							/>
						)}
					</div>

					{/* Detail panel */}
					<div className="lg:col-span-2">
						{selectedDetail ? (
							<div className="space-y-5">
								{/* Breadcrumb path */}
								<div className="bg-white rounded-xl border border-slate-200/60 p-5">
									<div className="flex items-center gap-2 mb-4">
										<div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
											<FolderTree className="w-5 h-5 text-primary" />
										</div>
										<div className="min-w-0">
											<h2 className="text-lg font-bold text-slate-800 truncate">
												{selectedDetail.name}
											</h2>
											<div className="flex items-center gap-1 text-xs text-slate-400 truncate">
												{selectedDetail.fullPath
													.split(" > ")
													.map((part, i, arr) => (
														<span
															key={part}
															className="flex items-center gap-1"
														>
															{i > 0 && (
																<ChevronRight className="w-3 h-3 text-slate-300" />
															)}
															<span
																className={
																	i === arr.length - 1
																		? "font-semibold text-primary"
																		: ""
																}
															>
																{part}
															</span>
														</span>
													))}
											</div>
										</div>
									</div>

									{/* Info grid */}
									<div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 pt-4 border-t border-slate-100">
										<div>
											<p className="text-[11px] text-slate-400 font-medium uppercase tracking-wide">
												Category ID
											</p>
											<p className="text-sm font-bold text-primary font-mono mt-1">
												#{selectedDetail.id}
											</p>
										</div>
										<div>
											<p className="text-[11px] text-slate-400 font-medium uppercase tracking-wide">
												Google ID
											</p>
											<p className="text-sm font-bold text-slate-700 font-mono mt-1">
												{selectedDetail.google_id ?? "—"}
											</p>
										</div>
										<div>
											<p className="text-[11px] text-slate-400 font-medium uppercase tracking-wide">
												Level
											</p>
											<p className="text-sm font-bold text-slate-700 mt-1">
												Depth {selectedDetail.level}
											</p>
										</div>
										<div>
											<p className="text-[11px] text-slate-400 font-medium uppercase tracking-wide">
												Children
											</p>
											<p className="text-sm font-bold text-slate-700 mt-1">
												{selectedDetail.childCount} sub-categories
											</p>
										</div>
									</div>
								</div>

								{/* Description */}
								{selectedDetail.description && (
									<div className="bg-white rounded-xl border border-slate-200/60 p-5">
										<h3 className="text-sm font-semibold text-slate-700 mb-2">
											Description
										</h3>
										<p className="text-sm text-slate-600">
											{selectedDetail.description}
										</p>
									</div>
								)}

								{/* Keywords */}
								{selectedDetail.keywords && (
									<div className="bg-white rounded-xl border border-slate-200/60 p-5">
										<h3 className="text-sm font-semibold text-slate-700 mb-2">
											Keywords
										</h3>
										<div className="flex flex-wrap gap-1.5">
											{selectedDetail.keywords.split(",").map((kw) => (
												<span
													key={kw.trim()}
													className="px-2.5 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600"
												>
													{kw.trim()}
												</span>
											))}
										</div>
									</div>
								)}

								{/* Children list */}
								{selectedDetail.childCount > 0 && (
									<div className="bg-white rounded-xl border border-slate-200/60 p-5">
										<h3 className="text-sm font-semibold text-slate-700 mb-3">
											Sub-categories ({selectedDetail.childCount})
										</h3>
										<div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
											{findChildren(tree, selectedDetail.id).map((child) => (
												<button
													key={child.id}
													type="button"
													onClick={() => handleSelect(child)}
													className="flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-50 hover:bg-primary/5 hover:border-primary/10 border border-transparent transition-all text-left group"
												>
													<Hash className="w-3.5 h-3.5 text-slate-300 group-hover:text-primary" />
													<div className="flex-1 min-w-0">
														<p className="text-sm font-medium text-slate-700 group-hover:text-primary truncate">
															{child.name}
														</p>
														<p className="text-[10px] font-mono text-slate-400">
															#{child.id}
														</p>
													</div>
													{child.children.length > 0 && (
														<span className="text-[10px] text-slate-400 bg-white px-1.5 py-0.5 rounded">
															{child.children.length} children
														</span>
													)}
												</button>
											))}
										</div>
									</div>
								)}

								{/* Full path card */}
								<div className="bg-white rounded-xl border border-slate-200/60 p-5">
									<h3 className="text-sm font-semibold text-slate-700 mb-3">
										Full Taxonomy Path
									</h3>
									<code className="block p-4 rounded-lg bg-slate-50 text-sm text-slate-600 font-mono break-all">
										{selectedDetail.fullPath}
									</code>
								</div>
							</div>
						) : (
							<div className="bg-white rounded-xl border border-slate-200/60 p-16 text-center h-[calc(100vh-280px)] flex flex-col items-center justify-center">
								<div className="w-20 h-20 rounded-2xl bg-slate-100 flex items-center justify-center mb-6">
									<FolderTree className="w-10 h-10 text-slate-300" />
								</div>
								<h3 className="text-xl font-semibold text-slate-700">
									Select a Category
								</h3>
								<p className="text-sm text-slate-400 mt-2 max-w-md">
									Browse the category tree on the left, or search by name or ID
									to view category details.
								</p>
							</div>
						)}
					</div>
				</div>
			)}

			{activeTab === "mappings" && <MappingsTab />}

			{activeTab === "missing" && <MissingMappingsTab />}
		</div>
	);
};

export default Categories;
